import { defineComponent, h, onMounted, shallowRef, TransitionGroup, watchEffect } from 'vue';

import { getCurrentInstance } from '@components/utils';

import { makeLocaleProps, useRender } from '../../../../composable';
import { makeUploadListProps } from '../../composable';

import ListItem from './ListItem';

import type { NUploader } from '../../interface';
import type { PropType } from 'vue';

const componentName = 'CdsUploadList';
export default defineComponent({
  name: componentName,
  inheritAttrs: false,
  props: {
    previewFile: {
      type: Function as PropType<NUploader.TPreviewFileHandler>,
      default: undefined
    },

    ...makeUploadListProps(),
    ...makeLocaleProps()
  },
  setup(props, { slots }) {
    const motionAppear = shallowRef(false);
    const instance = getCurrentInstance('upload-list');

    onMounted(() => {
      motionAppear.value = true;
    });

    watchEffect(() => {
      if (props.listType !== 'picture' && props.listType !== 'picture-card') return;

      ((props.items || []) as NUploader.IInternalUploadFile[]).forEach((file) => {
        if (
          typeof document === 'undefined' ||
          typeof window === 'undefined' ||
          !(window as any).FileReader ||
          !(window as any).File ||
          !(file.originFileObj instanceof File || (file.originFileObj as Blob) instanceof Blob) ||
          file.thumbUrl !== undefined
        )
          return;

        file.thumbUrl = '';

        if (props.previewFile) {
          props.previewFile(file.originFileObj).then((previewDataUrl: string) => {
            file.thumbUrl = previewDataUrl || '';
            instance.update();
          });
        }
      });
    });

    const onInternalPreview = (file: NUploader.IUploadFile, event?: Event) => {
      if (!props.onPreview) return;
      event?.preventDefault();
      return props.onPreview(file);
    };

    const onInternalDownload = (file: NUploader.IUploadFile) => {
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.download = file.name;
      a.href = file.url as string;
      a.rel = 'noopener noreferrer';
      a.target = '_blank';
      a.click();
      document.body.removeChild(a);
    };

    const onInternalClose = (file: NUploader.IUploadFile) => {
      props.onRemove?.(file);
    };

    useRender(() => {
      const {
        items = [],
        locale,
        progress,
        listType,
        showRemoveIcon,
        showDownloadIcon,
        showPreviewIcon,
        showTooltipWithError,
        itemRender,
        removeIcon,
        downloadIcon
      } = props;
      return h(
        TransitionGroup,
        { tag: 'div', name: 'slide-transition-y', class: 'cds-uploader-list' },
        {
          default: () =>
            items.map((file) => {
              const { uid: key } = file;

              return h(
                ListItem,
                {
                  key,
                  locale,
                  file,
                  items,
                  progress,
                  listType,
                  showPreviewIcon,
                  showDownloadIcon,
                  showRemoveIcon,
                  showTooltipWithError,
                  itemRender,
                  onPreview: onInternalPreview,
                  onDownload: onInternalDownload,
                  onRemove: onInternalClose,
                  removeIcon,
                  downloadIcon
                },
                {
                  itemRender: slots.itemRender
                }
              );
            })
        }
      );
    });

    return {
      handlePreview: onInternalPreview,
      handleDownload: onInternalDownload
    };
  }
});
